import { useCallback, useRef, useState } from 'react';

import { Field } from 'react-final-form';
import { useSelector } from 'react-redux';

import Preview from 'components/Pages/Admin/shared/Preview';
import FormGroupAdapter from 'components/shared/FormAdapters/FormDefault/FormGroup';
import Modal from 'components/shared/Modal';
import { Select } from 'components/shared/Select/adapters/FinalForm';
import { SafariContentOption } from 'components/shared/Select/options/SafariContent';
import { PAGE_STATUS_APPROVED } from 'constants/index';
import { accountIdSelector, isAgentSelector } from 'store/user/selectors';
import { axiosGetAutoCancelDecorator } from 'utils/axiosGetAutoCancelDecorator';

function PageMultiselect({
  name,
  onBlur,
  typesToSearch = [],
  placeholder,
  additionalQueryParams = {},
  onChange,
  closeMenuOnSelect,
  components,
  menuIsOpen,
  formatOptionLabel,
}) {
  const isAgent = useSelector(isAgentSelector);
  const accountId = useSelector(accountIdSelector);
  const selectRef = useRef();
  const [previewModalState, setPreviewModalState] = useState({ isOpen: false, pageId: null });

  const handleClosePreview = () => {
    setPreviewModalState({ isOpen: false });
  };
  const handleOpenPreview = (pageId) => {
    setPreviewModalState({ isOpen: true, pageId });
  };

  const decoratedAxiosGet = axiosGetAutoCancelDecorator();
  const fetchOptionsDebounced = async (q) => {
    const response = await decoratedAxiosGet(`/acc/${accountId}/pages/dropdown`, {
      params: {
        q,
        ...additionalQueryParams,
        account_id: accountId,
        types: typesToSearch,
        limit: 100,
        statuses: [PAGE_STATUS_APPROVED],
        order_by: 'handle',
        countless: '1',
      },
    });

    return response.data.data.map((item) => ({
      value: item.id,
      label: item.name,
      previewAvailable: item.currentStep === 'page',
      showContentIndicator: isAgent && (item.systemWide || item?.shared),
      shared: item?.shared,
      subtitle: item.tags.length > 0 ? `Tags: ${item?.tags.slice(0, 3).join(', ')}` : '',
      type: item.type,
    }));
  };

  const OptionMemoized = useCallback(
    (props) => (
      <SafariContentOption
        hasPreview={props.data.previewAvailable}
        onPreview={() => handleOpenPreview(props.data.value)}
        hasContentIndicator={props.data.showContentIndicator}
        shared={props.data?.shared}
        {...props}
      />
    ),
    [],
  );

  return (
    <>
      <Field
        /**
         * Using `key` with the dynamic value `JSON.stringify(additionalQueryParams)` ensures that
         * whenever `additionalQueryParams` changes, the `Select` component re-renders,
         * triggering a new `loadOptions` request. This is necessary to refresh the data
         * based on the updated query parameters.
         */
        key={JSON.stringify(additionalQueryParams)}
        component={FormGroupAdapter}
        customComponent={Select}
        name={name}
        isMulti
        sortable
        async
        onCustomChange={onChange}
        loadOptions={fetchOptionsDebounced}
        placeholder={placeholder ?? 'Please enter page name here'}
        closeMenuOnSelect={closeMenuOnSelect || false}
        isClearable
        openMenuOnFocus={true}
        noOptionsMessage={() => 'No pages found'}
        onBlur={onBlur}
        selectRef={selectRef}
        lockMenu={previewModalState.isOpen}
        styles={{
          container: () => ({ width: '100%' }),
        }}
        menuIsOpen={menuIsOpen}
        components={{
          Option: OptionMemoized,
          ...components,
        }}
        formatOptionLabel={formatOptionLabel}
      />

      <Modal noPadding size="lg" isOpen={previewModalState.isOpen} toClose={handleClosePreview}>
        {previewModalState.isOpen && <Preview pageId={previewModalState.pageId} />}
      </Modal>
    </>
  );
}

export default PageMultiselect;
